import { CheckCircle2, ShieldCheck, Stethoscope, Users } from "lucide-react";
import CTASection from "../components/sections/common/CTASection";
import FAQAccordion from "../components/sections/common/FAQAccordion";
import PageHeroSplit from "../components/sections/common/PageHeroSplit";
import ProcessSection from "../components/sections/common/ProcessSection";
import PageMeta from "../components/ui/PageMeta";
import LogoPlaceholder from "../components/ui/LogoPlaceholder";
import Section from "../components/ui/Section";
import SectionHeading from "../components/ui/SectionHeading";
import { ctaPhone, pageCtaContent } from "../data/ctaContent";
import { faqPreviewForAdmissions } from "../data/faqs";
import { admissionsPagesContent } from "../data/innerPages";
import {
  getCollegeDisplayName,
  getCollegeLogoByName,
  getCollegeStateByName
} from "../utils/collegeHelpers";

const content = admissionsPagesContent.mbbsIndia;

const whyGuidanceMatters = [
  "Medical admissions involve high-pressure timelines and significant family decisions.",
  "Students often need objective clarity when options feel overwhelming.",
  "Parents need transparent conversations before committing to a final direction."
];

const mbbsIndiaProcess = [
  {
    step: "01",
    title: "Understand medical pathway goals",
    description:
      "We begin with student intent, profile context, and family priorities to set a practical counselling base."
  },
  {
    step: "02",
    title: "Evaluate realistic admission directions",
    description:
      "Options are discussed through fit, feasibility, and decision clarity rather than rushed assumptions."
  },
  {
    step: "03",
    title: "Guide planning and documentation flow",
    description:
      "Families receive structured support for process milestones and application preparedness."
  },
  {
    step: "04",
    title: "Support final decision confidence",
    description:
      "Counselling continues through key decision points so students and parents stay aligned."
  }
];

const MBBS_INDIA_HERO_IMAGE =
  "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=1400&q=80";

function MbbsIndia() {
  return (
    <>
      <PageMeta title={content.meta.title} description={content.meta.description} />

      <PageHeroSplit
        badge={content.hero.badge}
        title={content.hero.title}
        description={content.hero.description}
        highlights={content.hero.highlights}
        primaryCta={{ label: "Speak with Counselling Team", to: "/contact" }}
        secondaryCta={{ label: "Explore Pathways", to: "/colleges" }}
        sideTitle="Who this path supports"
        sideDescription="Designed for high-trust MBBS discussions where student goals and family confidence both matter."
        sideItems={content.forWho}
        heroImage={MBBS_INDIA_HERO_IMAGE}
      />

      <Section className="bg-white">
        <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
          <article className="rounded-2xl border border-brand-line bg-white p-6 shadow-card sm:p-7">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-gold-strong">Overview</p>
            <p className="mt-3 text-base leading-relaxed text-brand-charcoal/84">{content.intro}</p>
            <div className="mt-6 rounded-xl border border-brand-line bg-brand-sky/35 p-4">
              <p className="text-sm leading-relaxed text-brand-charcoal/84">
                Guidance on this page is counselling-focused and decision-support oriented. It is intended to help
                families evaluate options with clarity and confidence.
              </p>
            </div>
          </article>

          <article className="rounded-2xl border border-brand-line bg-brand-sky/35 p-6 shadow-card sm:p-7">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white text-brand-navy">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <h2 className="mt-4 text-2xl font-semibold text-brand-navy">Why guided support matters here</h2>
            <ul className="mt-4 space-y-3">
              {whyGuidanceMatters.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm leading-relaxed text-brand-charcoal/84">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 text-brand-gold-strong" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </article>
        </div>
      </Section>

      <Section>
        <SectionHeading
          badge="How TheCareerSpace Helps"
          title="Structured support through major MBBS decision stages"
          description="Guidance is delivered with practical sequence so families can move forward without confusion."
        />

        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {content.supportBlocks.map((block) => (
            <article key={block.title} className="rounded-2xl border border-brand-line bg-white p-6 shadow-card">
              <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand-navy text-white">
                <Stethoscope className="h-4 w-4" />
              </div>
              <h3 className="mt-4 text-xl font-semibold text-brand-navy">{block.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-brand-charcoal/82">{block.description}</p>
            </article>
          ))}
        </div>

        <div className="mt-8 rounded-2xl border border-brand-line bg-white p-5 shadow-card sm:p-6">
          <p className="text-sm font-semibold text-brand-navy">Common decision points families bring to counselling</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {content.decisionPoints.map((point) => (
              <div key={point} className="rounded-xl border border-brand-line bg-brand-sky/35 px-3 py-2 text-sm text-brand-charcoal/84">
                {point}
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section className="bg-white">
        <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
          <SectionHeading
            badge="College Direction Preview"
            title="Sample institutions discussed during MBBS counselling context"
            description="A curated preview for discussion depth. The dedicated colleges page will expand this further."
          />

          <div className="grid gap-3 sm:grid-cols-2">
            {content.collegePreview.map((college) => {
              const collegeName = getCollegeDisplayName(college);
              const logoUrl = getCollegeLogoByName(college);
              const collegeState = getCollegeStateByName(college);

              return (
                <article key={college} className="rounded-xl border border-brand-line bg-white px-4 py-3 shadow-card">
                  <div className="flex items-center gap-3">
                    {logoUrl ? (
                      <img
                        src={logoUrl}
                        alt={`${collegeName} logo`}
                        className="h-10 w-10 rounded-lg border border-brand-line bg-white object-contain"
                      />
                    ) : (
                      <LogoPlaceholder
                        name={collegeName}
                        className="h-10 w-10 rounded-lg text-[11px]"
                      />
                    )}
                    <div>
                      <p className="text-sm font-semibold text-brand-navy">{collegeName}</p>
                      <p className="text-xs text-brand-charcoal/78">{collegeState}</p>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </Section>

      <Section>
        <div className="grid gap-5 md:grid-cols-2">
          <article className="rounded-2xl border border-brand-line bg-white p-6 shadow-card">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-gold-soft text-brand-navy">
              <Users className="h-5 w-5" />
            </div>
            <h3 className="mt-4 text-xl font-semibold text-brand-navy">For students</h3>
            <p className="mt-2 text-sm leading-relaxed text-brand-charcoal/82">
              Receive calm, structured support so choices are made with better understanding and reduced pressure.
            </p>
          </article>

          <article className="rounded-2xl border border-brand-line bg-brand-sky/35 p-6 shadow-card">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white text-brand-navy">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <h3 className="mt-4 text-xl font-semibold text-brand-navy">For parents</h3>
            <p className="mt-2 text-sm leading-relaxed text-brand-charcoal/82">
              Get transparent counselling conversations that prioritize clarity, practicality, and responsible decisions.
            </p>
          </article>
        </div>
      </Section>

      <ProcessSection
        badge="MBBS Guidance Flow"
        title="How TheCareerSpace supports MBBS in India planning"
        description="A clear process for high-stakes admission journeys."
        steps={mbbsIndiaProcess}
      />

      <Section className="bg-white">
        <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <SectionHeading
            badge="FAQ Preview"
            title="Common MBBS counselling clarifications"
            description="Quick answers families typically need before beginning."
          />
          <FAQAccordion items={faqPreviewForAdmissions} />
        </div>
      </Section>

      <CTASection
        {...pageCtaContent.mbbsIndia}
        phone={ctaPhone}
        secondaryCtaClassName="border-brand-gold/55 bg-brand-gold-soft text-brand-navy hover:border-brand-gold hover:bg-brand-gold hover:text-brand-navy"
      />
    </>
  );
}

export default MbbsIndia;
